<script setup lang="ts">
import { computed } from 'vue';
import type { ApiImage } from '@/types/images';
import { buildSrcSet, isOptimisedImage } from '@/types/images';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';

const props = withDefaults(
  defineProps<{
    image: ApiImage | null | undefined;
    alt: string;
    /** Value for the inner `<img sizes>` attribute (responsive srcset). */
    sizes?: string;
    loading?: 'lazy' | 'eager';
    /** Pass through for LCP / priority hints (omit when not needed). */
    fetchPriority?: 'high' | 'low' | 'auto';
    decoding?: 'async' | 'auto' | 'sync';
  }>(),
  {
    sizes: '100vw',
    loading: 'lazy',
    decoding: 'async',
  }
);

const legacyUrl = computed((): string | null => {
  const img = props.image;
  if (!img) return null;
  if (isOptimisedImage(img)) return img.fallback?.url ?? null;
  return typeof (img as { url?: string }).url === 'string' ? (img as { url: string }).url : null;
});
</script>

<template>
  <picture
    v-if="image && isOptimisedImage(image)"
    class="api-responsive-image"
  >
    <source
      v-for="source in image.sources"
      :key="source.media_type"
      :type="source.media_type"
      :srcset="buildSrcSet(source.src_set)"
    />
    <img
      :src="image.fallback!.url"
      :srcset="buildSrcSet(image.fallback!.src_set)"
      :sizes="sizes"
      :alt="alt"
      class="api-responsive-image__img"
      :loading="loading"
      :fetchpriority="fetchPriority"
      :decoding="decoding"
      @error="onOfflineMediaImgError"
    />
  </picture>
  <img
    v-else-if="legacyUrl"
    :src="legacyUrl"
    :alt="alt"
    class="api-responsive-image__img"
    :loading="loading"
    :fetchpriority="fetchPriority"
    :decoding="decoding"
    @error="onOfflineMediaImgError"
  />
</template>

<style scoped>
.api-responsive-image {
  display: block;
  width: 100%;
  height: 100%;
}

.api-responsive-image__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
</style>
